import type * as api from '@gitbook/api-client';
import type { NavigationScreenSiteState } from '@gitbook/app-navigation';
import { isLegacySiteType } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbItem,
    Breadcrumbs,
    Button,
    ButtonTab,
    ButtonTabs,
    DialogRoot,
    DialogTrigger,
    HeaderBar,
    HeaderBarActions,
    HeaderBarActionsMain,
    HeaderBarActionsSide,
    HeaderBarBreadcrumbs,
    HeaderBarMain,
    HeaderBarTabs,
} from '@gitbook/spine-react';

import {
    getDefaultSiteSpaceInSiteStructure,
    SiteContentIcon,
    SiteTypeBadge,
    SiteTypeHoverCard,
} from '../../../Components';
import { checkIsSiteStructureEmpty, type SiteCapability } from '../../../state';
import { MembersAccessDialog } from './MembersAccessModal';
import { SitePublishButton } from './Plan';

/**
 * Header for the site screen.
 */
export function SiteHeader(props: {
    screen: NavigationScreenSiteState & { organization: string };
    organization: api.Organization;
    site: api.Site;
    sitePermissions: SiteCapability;
    siteStructure: api.SiteStructure;
}) {
    const { screen, organization, site, sitePermissions, siteStructure } = props;
    const isEmpty = checkIsSiteStructureEmpty(siteStructure);
    const defaultSiteSpace = getDefaultSiteSpaceInSiteStructure(siteStructure);

    return (
        <HeaderBar data-testid="site.screen">
            <HeaderBarMain>
                <HeaderBarBreadcrumbs>
                    <SiteBreadcrumbs screen={screen} organization={organization} site={site} />
                </HeaderBarBreadcrumbs>
                {isEmpty ? null : (
                    <HeaderBarTabs>
                        <ButtonTabs>
                            <ButtonTab
                                label="Overview"
                                active={!screen.tab && !screen.sideSheet}
                                title="Overview of your site's content and settings"
                                href={{
                                    screen: {
                                        type: 'site',
                                        site: site.id,
                                        tab: undefined,
                                        sideSheet: undefined,
                                    },
                                }}
                            />
                            {defaultSiteSpace ? (
                                <ButtonTab
                                    label="Editor"
                                    active={false}
                                    title="Edit your site's content"
                                    href={{
                                        screen: {
                                            type: 'space',
                                            space: defaultSiteSpace.space.id,
                                        },
                                    }}
                                />
                            ) : null}
                            <ButtonTab
                                label="Insights"
                                title="Explore your site's traffic and engagement"
                                active={screen.tab?.type === 'insights'}
                                href={{
                                    screen: {
                                        type: 'site',
                                        site: site.id,
                                        tab: { type: 'insights', report: 'traffic' },
                                    },
                                }}
                            />
                            {sitePermissions.canAdminSite && (
                                <ButtonTab
                                    label="Customization"
                                    active={screen.tab?.type === 'customization'}
                                    title="Customize your site's appearance and behavior"
                                    href={{
                                        screen: {
                                            type: 'site',
                                            site: site.id,
                                            tab: { type: 'customization', tab: 'general' },
                                        },
                                    }}
                                />
                            )}
                            <ButtonTab
                                label="Preview"
                                title="Preview your site's appearance and behavior"
                                active={screen.tab?.type === 'preview'}
                                href={{
                                    screen: {
                                        type: 'site',
                                        site: site.id,
                                        tab: { type: 'preview' },
                                    },
                                }}
                            />
                            {sitePermissions.canAdminSite && (
                                <ButtonTab
                                    label="Settings"
                                    active={screen.tab?.type === 'settings'}
                                    title="Manage your site's settings"
                                    href={{
                                        screen: {
                                            type: 'site',
                                            site: site.id,
                                            tab: { type: 'settings' },
                                        },
                                    }}
                                />
                            )}
                        </ButtonTabs>
                    </HeaderBarTabs>
                )}
            </HeaderBarMain>
            {isEmpty ? null : (
                <HeaderBarActions>
                    <HeaderBarActionsSide>
                        {sitePermissions.canAdminSite ? (
                            <IntegrationsButton
                                siteId={site.id}
                                organizationId={screen.organization}
                            />
                        ) : null}
                        {sitePermissions.canAdminSite ? <MemberAccessButton /> : null}
                    </HeaderBarActionsSide>
                    <HeaderBarActionsMain>
                        <SitePublishButton
                            site={site}
                            organization={organization}
                            canAdminSite={sitePermissions.canAdminSite}
                        />
                    </HeaderBarActionsMain>
                </HeaderBarActions>
            )}
        </HeaderBar>
    );
}

function SiteBreadcrumbs(props: {
    screen: NavigationScreenSiteState;
    organization: api.Organization;
    site: api.Site;
}) {
    const { site, organization } = props;

    return (
        <Breadcrumbs>
            <BreadcrumbItem
                icon={Icon.DocsSite}
                label="Docs sites"
                href={{
                    screen: {
                        type: 'sites',
                        organization: props.screen.organization,
                    },
                }}
            />
            <BreadcrumbItem
                href={{
                    screen: { ...props.screen, tab: undefined },
                }}
                icon={<SiteContentIcon site={site} />}
            >
                <div className="flex items-center gap-2 truncate">
                    <span className="truncate">{site.title}</span>
                    {!isLegacySiteType(site.type) ? (
                        <SiteTypeHoverCard organization={organization} site={site}>
                            <SiteTypeBadge type={site.type} />
                        </SiteTypeHoverCard>
                    ) : null}
                </div>
            </BreadcrumbItem>
        </Breadcrumbs>
    );
}

function IntegrationsButton(props: { siteId: string; organizationId: string }) {
    return (
        <Button
            kind="blank"
            iconOnly
            label="Integrations"
            title="Install and configure integrations for this site"
            leadingIcon={Icon.Integrations}
            href={{
                screen: { type: 'site', site: props.siteId, sideSheet: { type: 'integrations' } },
            }}
        />
    );
}

function MemberAccessButton() {
    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    kind="blank"
                    size="medium"
                    iconOnly
                    leadingIcon={Icon.Users}
                    label="Permissions"
                    title="Review who can access and manage this site"
                />
            </DialogTrigger>
            <MembersAccessDialog />
        </DialogRoot>
    );
}
